import { useParams, useNavigate } from "react-router-dom";
import {
  useGetMemberMessageDetailQuery,
  useMarkMessageAsReadMutation,
} from "../../../store/member/member-communication";
import { CardComp, ButtonComp } from "../../../components";
import { PageTitle } from "../../Admin/modules/PageHeaders";
import { Loading } from "../../../components";
import { getStatusBadgeColor } from "../../../utils/communicationUtils";
import { convertToTitleCase } from "../../../utils/helpers";
import moment from "moment";
import styled from "styled-components";
import { toast } from "react-hot-toast";
import PageWrapper from "../modules/PageWrapper";

export const MessageDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: message, isLoading, error } = useGetMemberMessageDetailQuery(
    id,
    {
      skip: !id,
    }
  );

  const [markAsRead, { isLoading: isMarkingAsRead }] =
    useMarkMessageAsReadMutation();

  const handleMarkAsRead = async () => {
    if (!id) return;
    try {
      await markAsRead(id).unwrap();
      toast.success("Message marked as read");
    } catch (error) {
      toast.error(
        error?.data?.message || "Failed to mark message as read"
      );
    }
  };

  if (isLoading) {
    return (
      <PageWrapper title={"Message Detail"}>
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ minHeight: "300px" }}
        >
          <Loading />
        </div>
      </PageWrapper>
    );
  }

  if (error || !message) {
    return (
      <PageWrapper title={"Message Detail"}>
        <div className="alert alert-danger">
          Failed to load message. Please try again.
        </div>
        <ButtonComp
          btnText={"Back to Messages"}
          btnClassName={"py-2 px-3 mt-3"}
          onClick={() => navigate("/member/communication")}
        />
      </PageWrapper>
    );
  }

  const isRead =
    message?.is_read === true ||
    message?.is_read === 1 ||
    message?.read_at !== null;
  const deliveryStatus =
    message?.member_delivery_status ||
    message?.delivery_status ||
    message?.status ||
    "";
  const statusColor = getStatusBadgeColor(deliveryStatus);

  return (
    <Wrapper>
      <PageWrapper title={message?.header || "Message Detail"}>
        <div className="d-flex align-items-center gap-3 mb-4">
          <button
            type="button"
            className="btn btn-link p-0 text-decoration-none"
            onClick={() => navigate(-1)}
            style={{ color: "var(--color-primary)" }}
          >
            ← Back
          </button>
        </div>

        <CardComp cardClassName={"mb-4"}>
          <div className="px-3 pb-4">
            <div className="row g-3 mb-3">
              <div className="col-12 col-md-6">
                <small className="text-muted d-block">Date Sent</small>
                <span>
                  {message?.created_at
                    ? moment(message.created_at).format("DD-MMM-YYYY HH:mm")
                    : "-"}
                </span>
              </div>
              <div className="col-12 col-md-6">
                <small className="text-muted d-block">Channel</small>
                <span>
                  {message?.sending_mode
                    ? convertToTitleCase(
                        (message.sending_mode || "").replace("Mode", "")
                      )
                    : "-"}
                </span>
              </div>
              <div className="col-12 col-md-6">
                <small className="text-muted d-block">Sent by</small>
                <span>
                  {message?.created_by_staff
                    ? `${message.created_by_staff.first_name || ""} ${
                        message.created_by_staff.last_name || ""
                      }`.trim() || `Staff #${message.created_by}`
                    : message?.created_by
                    ? `Staff #${message.created_by}`
                    : "-"}
                </span>
              </div>
              <div className="col-12 col-md-6">
                <small className="text-muted d-block">Delivery Status</small>
                <span
                  className="badge rounded-pill"
                  style={{
                    backgroundColor: statusColor,
                    color: "#fff",
                    padding: "4px 10px",
                  }}
                >
                  {convertToTitleCase(deliveryStatus || "Unknown")}
                </span>
              </div>
              <div className="col-12 col-md-6">
                <small className="text-muted d-block">Read Status</small>
                <span
                  className="badge rounded-pill"
                  style={{
                    backgroundColor: isRead ? "#19BC31" : "#FF9500",
                    color: "#fff",
                    padding: "4px 10px",
                  }}
                >
                  {isRead ? "Read" : "Unread"}
                </span>
              </div>
              {message?.read_at && (
                <div className="col-12 col-md-6">
                  <small className="text-muted d-block">Read At</small>
                  <span>
                    {moment(message.read_at).format("DD-MMM-YYYY HH:mm")}
                  </span>
                </div>
              )}
            </div>

            <hr />

            <div className="mb-4">
              <h6 className="fw-bold mb-2">Message Content</h6>
              <div
                className="p-3 rounded"
                style={{
                  backgroundColor: "#f7f7f7",
                  border: "1px solid #e0e0e0",
                }}
              >
                {message?.content_email ? (
                  <div
                    dangerouslySetInnerHTML={{ __html: message.content_email }}
                  />
                ) : message?.body ? (
                  <div
                    dangerouslySetInnerHTML={{ __html: message.body }}
                  />
                ) : (
                  <p className="mb-0">-</p>
                )}
              </div>
            </div>

            {!isRead && (
              <div className="d-flex justify-content-end mt-4">
                <ButtonComp
                  btnText={
                    isMarkingAsRead ? "Marking as read..." : "Mark as Read"
                  }
                  btnClassName={"py-2 px-4 rounded text-white fw-bold border-0"}
                  ButtonInlineStyle={{
                    backgroundColor: isMarkingAsRead ? "#a1a0a0" : "var(--color-primary)",
                    minWidth: "150px",
                    transition: "all 0.3s ease",
                  }}
                  onClick={handleMarkAsRead}
                  loading={isMarkingAsRead}
                  disabled={isMarkingAsRead}
                />
              </div>
            )}
          </div>
        </CardComp>
      </PageWrapper>
    </Wrapper>
  );
};

const Wrapper = styled.section`
  .table th {
    font-weight: 600;
    padding: 10px;
  }
  .table td {
    padding: 10px;
    border-color: #e0e0e0;
  }
`;

export default MessageDetail;
